import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const TYPE_LABEL = {
    pre: "Pré-formation (positionnement)",
    post_acquis: "Post-acquis (compétences)",
    satisfaction_chaud: "Satisfaction à chaud",
};
const TYPE_DESC = {
    pre: "Rempli AVANT la session — adapter la journée aux attentes.",
    post_acquis: "Rempli APRÈS la session — valider les compétences acquises.",
    satisfaction_chaud: "Rempli FIN de session — Qualiopi obligatoire.",
};

export default function FormateurEvaluations() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/formateur/evaluations");
                setItems(data);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div className="mx-auto max-w-6xl px-6 py-10" data-testid="page-formateur-evaluations">
            <div className="flex items-start justify-between gap-6">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">Qualiopi · Évaluations</div>
                    <h1 className="mt-2 font-display text-5xl font-normal tracking-tightest">Mes évaluations</h1>
                    <p className="mt-3 max-w-2xl text-brand-smoke">
                        Pour chacune de vos formations, configurez les 3 questionnaires Qualiopi : positionnement,
                        acquis pédagogiques et satisfaction à chaud. Sans personnalisation, un template Qualiopi
                        par défaut est utilisé.
                    </p>
                </div>
                <div className="hidden h-16 w-16 items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange md:flex">
                    <ClipboardList className="h-7 w-7" />
                </div>
            </div>

            {loading ? (
                <div className="mt-12 text-center text-brand-smoke">Chargement…</div>
            ) : items.length === 0 ? (
                <div className="mt-12 rounded-2xl border border-dashed border-brand-line bg-white p-10 text-center text-brand-smoke">
                    Vous n'êtes assigné à aucune formation pour l'instant.
                </div>
            ) : (
                <div className="mt-10 space-y-5">
                    {items.map((it) => (
                        <div
                            key={it.formation.slug}
                            className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium"
                            data-testid={`eval-formation-${it.formation.slug}`}
                        >
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div>
                                    <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">
                                        Cursus {it.formation.cursus_slug}
                                    </div>
                                    <h2 className="mt-1 font-display text-2xl text-brand-ink">{it.formation.name}</h2>
                                </div>
                            </div>
                            <div className="mt-5 grid gap-3 md:grid-cols-3">
                                {(["pre", "post_acquis", "satisfaction_chaud"]).map((t) => {
                                    const cust = it.evaluations[t]?.customized;
                                    return (
                                        <Link
                                            key={t}
                                            to={`/formateur/evaluations/${it.formation.slug}/${t}`}
                                            data-testid={`eval-edit-${it.formation.slug}-${t}`}
                                            className="group flex flex-col rounded-xl border border-brand-line bg-[#FAFAFA] p-4 transition hover:border-brand-orange hover:bg-white"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                                                    {TYPE_LABEL[t]}
                                                </div>
                                                {cust ? (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700">
                                                        <CheckCircle2 className="h-3 w-3" /> Personnalisé
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-brand-orange">
                                                        <AlertCircle className="h-3 w-3" /> Template par défaut
                                                    </span>
                                                )}
                                            </div>
                                            <p className="mt-2 flex-1 text-sm text-brand-ink/80">{TYPE_DESC[t]}</p>
                                            <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-orange">
                                                Configurer <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
